import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { ReactNode } from "react";

import Layout from "../components/layouts/layout.comp";
import Error from "../components/reuseables/error.comp";
import localeCore from "../core/locale.core";
import mailTemplateCore from "../core/mailTemplate.core";
import { useTranslation } from "../core/translation.core";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../icons/loadingOvalCenter.icon";
import { MailTemplate } from "../types/mailTemplate";
import { Product } from "../types/product";
import AddLink from "../components/form/addLink.comp";

export default function MailTemplatesPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.mailTemplate.mailTemplate)} titleBtn={<AddLink id="mailTemplate-create" href="/mailTemplate/create" />}>
      <Error />

      <section data-type="section">
        <MailTemplateList />
      </section>
    </Layout>
  );
}

const MailTemplateList = () => {
  const { t, msg } = useTranslation();
  const query = useQuery({
    queryKey: ["mailTemplates"],
    queryFn: mailTemplateCore.loadMailTemplates,
  });

  if (query.isError) return <ErrorCenterIcon reason={query.failureReason} />;
  if (query.isLoading) return <LoadingOvalCenterIcon />;
  if (!query.data) return null;

  return (
    <div id="order-section">
      <div className="grid grid-cols-[200px_100px] lg:grid-cols-[200px_100px_1fr_1fr_1fr] gap-6 border-b py-3 justify-items-start">
        <div>{t(msg.mailTemplate.type)}</div>
        <div></div>
        <div className="hidden lg:block">{t(msg.mailTemplate.toList)}</div>
        <div className="hidden lg:block">{t(msg.mailTemplate.ccList)}</div>
        <div className="hidden lg:block">{t(msg.mailTemplate.bccList)}</div>
      </div>

      {query.data.map((mailTemplate) => (
        <TemplateRow mailTemplate={mailTemplate} key={mailTemplate.id} />
      ))}
    </div>
  );
};

export function TemplateRow({ mailTemplate }: { mailTemplate: MailTemplate }) {
  const { t, msg } = useTranslation();

  return (
    <div
      attr-name="orders"
      attr-type="order-row"
      className="grid grid-cols-[200px_100px] lg:grid-cols-[200px_100px_1fr_1fr_1fr] gap-6 border-b py-3 items-center"
    >
      <div>{mailTemplateCore.getMailTemplateTypeTitle(mailTemplate.templateType, { t, msg })}</div>
      <div>
        <Link
          className="product-edit text-white bg-her-dodgerblue px-3 py-1 rounded-md outline-none cursor-pointer float-right"
          href={`mailTemplate/${mailTemplate.id}`}
        >
          {t(msg.general.view)}
        </Link>
      </div>
      <EmailList emails={mailTemplate.toList} />
      <EmailList emails={mailTemplate.ccList} />
      <EmailList emails={mailTemplate.bccList} />
    </div>
  );
}

function EmailList(emails: { emails?: string[] }) {
  return (
    <div className="hidden lg:block self-start justify-self-start">
      {emails.emails?.map((email, index) => (
        <div key={index}>{email}</div>
      ))}
    </div>
  );
}
export function ProductList({ product }: { product: Product }) {
  // const selectedShop = useAppSelector(selectSelectedShop);

  // if (!selectedShop) return null;

  const imageClass = "basis-1/6 lg:basis-1/12 " + "grow shrink";
  const containerClass = "basis-4/6  lg:basis-11/12 " + "flex flex-row " + "flex-wrap lg:flex-nowrap gap-2 lg:gap-6";
  // const detailClass = "basis-full lg:basis-4/12 " + "grow shrink text-base text-slate-800 font-semibold text-left";
  const categoryClass = "hidden lg:block " + "grow shrink basis-2/12";
  const priceClass = "basis-1/3 lg:basis-1/12 " + "grow shrink text-slate-800 font-semibold";
  const skuClass = "hidden lg:block " + "grow-0 lg:grow shrink basis-1/12";
  const actionClass = "basis-1/3 lg:basis-[42px] " + "grow-0 shrink";

  return (
    <div
      attr-name={localeCore.getLocaleValueDefault(product.name)}
      attr-sku={product.sku}
      attr-type="product-row"
      className="grid grid-cols-6 items-center py-3 border-b-[1px]"
    >
      <div className={imageClass}>
        {product.featureMedia ? (
          <Image
            className="w-full"
            src={product.featureMedia.thumbnailUrl}
            width={100}
            height={100}
            alt={localeCore.getLocaleValueDefault(product.name) ?? ""}
          />
        ) : null}
      </div>
      <div className={containerClass}>
        <div className={categoryClass}>{product.sku}</div>
        <div className={priceClass}>${product.price}</div>
        <div className={skuClass}>{product.sku}</div>
        <div className={actionClass}>
          <Link
            className="product-edit text-white bg-slate-500 px-2 py-1 rounded outline-none cursor-pointer float-right"
            href={`product/edit/${product.id}`}
          >
            Edit
          </Link>
        </div>
      </div>
    </div>
  );
}
